import { useState, useEffect } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { Confirm } from '../components/Confirm';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

interface User {
  id: number;
  username: string;
  role: string;
  roleId: number;
  activo: boolean;
  /** Ayuda interactiva (guías paso a paso en los formularios). */
  ayudaInteractiva?: boolean;
  createdAt: string;
}

interface RoleOption {
  id: number;
  nombre: string;
  descripcion?: string;
}

interface UserListResponse {
  data: User[];
  total: number;
}

// Color mapping by role name (stays visual, works for any role name)
function roleColorClass(nombre: string): string {
  const key = (nombre || '').toLowerCase();
  if (key === 'admin') return 'bg-purple-100 text-purple-700';
  if (key === 'operador') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-600';
}

// Pretty role label (capitalize first letter)
function roleLabel(nombre: string): string {
  return nombre ? nombre.charAt(0).toUpperCase() + nombre.slice(1) : nombre;
}

const emptyForm = () => ({
  username: '',
  password: '',
  roleId: 0,
  ayudaInteractiva: true,
});

export function UsuariosPage() {
  const { data: response, loading, refetch } = useApi<UserListResponse | User[]>('/api/v3/users');
  const data = Array.isArray(response) ? response : (response?.data ?? []);

  // Fetch roles from API for the select and filter chips
  const [roles, setRoles] = useState<RoleOption[]>([]);
  useEffect(() => {
    api.get<RoleOption[]>('/api/v3/permisos/roles')
      .then(res => setRoles(res || []))
      .catch(() => setRoles([]));
  }, []);

  // Map roleId → role name for display in the table
  const roleNameMap = new Map<number, string>();
  for (const r of roles) {
    roleNameMap.set(r.id, r.nombre);
  }

  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = data.filter(row =>
    Object.entries(filters).every(([key, val]) => {
      if (!val) return true;
      if (key === 'activo') {
        const target = val === 'true' || val === 'si' || val === 'sí';
        return row.activo === target;
      }
      if (key === 'roleId') {
        return String(row.roleId) === val;
      }
      return String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase());
    }),
  );

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [roleError, setRoleError] = useState('');
  const cerrarForm = () => { setOpen(false); setEditId(null); setForm(emptyForm()); setUsernameError(''); setPasswordError(''); setRoleError(''); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);
  const toast = useToast();
  const { can } = useAuth();

  const handleSave = async () => {
    if (!form.username.trim()) {
      setUsernameError('El nombre de usuario es obligatorio');
      toast.addToast('El nombre de usuario es obligatorio', 'error');
      return;
    }
    setUsernameError('');
    if (!editId && !form.password.trim()) {
      setPasswordError('La contraseña es obligatoria');
      toast.addToast('La contraseña es obligatoria', 'error');
      return;
    }
    setPasswordError('');
    if (!form.roleId || form.roleId <= 0) {
      setRoleError('Seleccioná un rol');
      toast.addToast('Seleccioná un rol', 'error');
      return;
    }
    setRoleError('');
    setSubmitting(true);
    try {
      if (editId) {
        const body: Record<string, unknown> = {
          username: form.username.trim(),
          roleId: form.roleId,
        };
        if (form.password.trim()) {
          body.password = form.password;
        }
        // Sólo si el admin la cambió: así no pisa la que el usuario cambió desde su menú mientras tanto.
        const ayudaAntes = data.find(u => u.id === editId)?.ayudaInteractiva ?? true;
        if (form.ayudaInteractiva !== ayudaAntes) body.ayudaInteractiva = form.ayudaInteractiva;
        await api.patch(`/api/v3/users/${editId}`, body);
        toast.addToast('Usuario actualizado', 'success');
      } else {
        await api.post('/api/v3/users', {
          username: form.username.trim(),
          password: form.password,
          roleId: form.roleId,
        });
        toast.addToast('Usuario creado', 'success');
      }
      cerrarForm();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item: User) => {
    if (!can('usuarios', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setForm({
      username: item.username,
      password: '',
      roleId: item.roleId || 0,
      ayudaInteractiva: item.ayudaInteractiva ?? true,
    });
    setEditId(item.id);
    setUsernameError('');
    setPasswordError('');
    setRoleError('');
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!can('usuarios', 'eliminar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setSubmitting(true);
    try {
      await api.delete(`/api/v3/users/${deleteTarget.id}`);
      toast.addToast(`"${deleteTarget.username}" eliminado`, 'success');
      setConfirmDelete(false);
      setDeleteTarget(null);
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'username',
      header: 'Usuario',
      help: 'Nombre de usuario para iniciar sesión',
    },
    {
      key: 'role',
      header: 'Rol',
      render: (r: User) => {
        const name = roleNameMap.get(r.roleId) || r.role;
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColorClass(name)}`}>
            {roleLabel(name)}
          </span>
        );
      },
      help: 'Admin: acceso total. Operador: gestión de compras y stock. Visualizador: solo lectura.',
    },
    {
      key: 'activo',
      header: 'Activo',
      render: (r: User) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {r.activo ? 'Sí' : 'No'}
        </span>
      ),
      filterable: false,
      help: 'Los usuarios inactivos no pueden iniciar sesión',
    },
    {
      key: 'actions',
      header: '',
      render: (r: User) => (
        <button
          onClick={e => {
            e.stopPropagation();
            setDeleteTarget(r);
            setConfirmDelete(true);
          }}
          disabled={!can('usuarios', 'eliminar')}
          className="text-red-500 hover:text-red-700 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title={!can('usuarios', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar usuario'}
        >
          Eliminar
        </button>
      ),
      filterable: false,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Usuarios</h1>
          <HelpTip
            tips={[
              'Gestioná los usuarios que acceden al sistema ERP',
              'Admin: acceso total a todas las funcionalidades',
              'Operador: gestión de compras, stock y facturación',
              'Visualizador: acceso de solo lectura a informes y catálogos',
              'Los usuarios eliminados se desactivan (borrado lógico)',
            ]}
          />
          <p className="text-slate-500 text-sm mt-1">Gestión de usuarios del sistema</p>
        </div>
        <Button
          onClick={() => {
            setEditId(null);
            setForm(emptyForm());
            setUsernameError('');
            setPasswordError('');
            setRoleError('');
            setOpen(true);
          }}
          disabled={!can('usuarios', 'crear')}
          title={!can('usuarios', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          + Nuevo Usuario
        </Button>
      </div>

      {/* Quick filter chips for activo and role */}
      <div className="flex items-center gap-2 -mt-2 flex-wrap">
        <span className="text-xs text-slate-400">Filtros rápidos:</span>
        {['', 'true', 'false'].map(val => (
          <button
            key={`activo-${val}`}
            onClick={() =>
              setFilters(f =>
                val
                  ? { ...f, activo: val }
                  : Object.fromEntries(Object.entries(f).filter(([k]) => k !== 'activo')),
              )
            }
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              filters.activo === val && val !== ''
                ? 'bg-brand-100 text-brand-700'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {val === '' ? 'Todos' : val === 'true' ? '✓ Activos' : '✕ Inactivos'}
          </button>
        ))}
        {roles.length > 0 && (
          <>
            <span className="text-slate-300 mx-1">|</span>
            <button
              onClick={() =>
                setFilters(f =>
                  Object.fromEntries(Object.entries(f).filter(([k]) => k !== 'roleId')),
                )
              }
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                !filters.roleId
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              Todos
            </button>
            {roles.map(r => (
              <button
                key={`role-${r.id}`}
                onClick={() =>
                  setFilters(f => ({
                    ...f,
                    roleId: String(r.id),
                  }))
                }
                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                  filters.roleId === String(r.id)
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {roleLabel(r.nombre)}
              </button>
            ))}
          </>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={handleEdit}
        filters={filters}
        onFilterChange={(k, v) =>
          setFilters(f =>
            v
              ? { ...f, [k]: v }
              : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)),
          )
        }
        title="Usuarios"
      />

      <Modal
        open={open}
        cierre={cierreForm}
        title={editId ? `Editar Usuario #${editId}` : 'Nuevo Usuario'}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Usuario</label>
            <input
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Nombre de usuario"
              autoFocus
            />
            {usernameError && <p className="text-red-600 text-sm">{usernameError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Contraseña{editId ? ' (dejar vacío para no cambiar)' : ''}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={editId ? '••••••••' : 'Contraseña'}
            />
            {passwordError && <p className="text-red-600 text-sm">{passwordError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Rol</label>
            <select
              value={form.roleId || ''}
              onChange={e => setForm({ ...form, roleId: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="" disabled>
                Seleccionar rol...
              </option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {roleLabel(r.nombre)}
                </option>
              ))}
            </select>
            {roleError && <p className="text-red-600 text-sm">{roleError}</p>}
          </div>
          {/* Un usuario nuevo arranca con la ayuda encendida (default de la columna). */}
          {editId && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.ayudaInteractiva}
                  onChange={e => setForm({ ...form, ayudaInteractiva: e.target.checked })}
                  aria-describedby="usuario-ayuda-detalle"
                />
                Ayuda interactiva
              </label>
              <p id="usuario-ayuda-detalle" className="text-xs text-slate-500 ml-6">Guías paso a paso en los formularios. El usuario también la cambia desde su menú.</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={cierreForm.cerrar}
              className="px-4 py-2 text-sm"
            >
              Cancelar
            </button>
            <Button
              onClick={handleSave}
              loading={submitting}
              disabled={editId ? !can('usuarios', 'editar') : !can('usuarios', 'crear')}
              title={(editId ? !can('usuarios', 'editar') : !can('usuarios', 'crear')) ? 'No tenés permiso para realizar esta acción' : undefined}
            >
              {submitting ? 'Guardando...' : editId ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={confirmDelete}
        title="Eliminar Usuario"
        message={`¿Estás seguro de eliminar "${deleteTarget?.username}"? El usuario será desactivado (borrado lógico).`}
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
